import React, { useState } from 'react';

// Campaign type definitions with their descriptions and default settings
const CAMPAIGN_TYPES = {
  general: {
    name: 'General Outreach',
    description: 'Standard outreach campaign for general purposes',
    icon: 'mail'
  },
  anniversary: {
    name: 'Anniversary Recognition',
    description: 'Celebrate work anniversaries and milestones',
    icon: 'cake',
    suggestedTags: ['anniversary', 'recognition', 'milestone']
  },
  newsletter: {
    name: 'Newsletter',
    description: 'Regular updates and news for alumni',
    icon: 'newspaper',
    suggestedTags: ['newsletter', 'updates', 'news']
  },
  nurture: {
    name: 'Relationship Nurturing',
    description: 'Long-term engagement to maintain relationships',
    icon: 'heart',
    suggestedTags: ['nurture', 'relationship', 'engagement']
  },
  recruitment: {
    name: 'Talent Recruitment',
    description: 'Recruiting alumni back to the organization',
    icon: 'briefcase',
    suggestedTags: ['recruitment', 'hiring', 'talent']
  },
  event: {
    name: 'Event Invitation',
    description: 'Invite alumni to upcoming events',
    icon: 'calendar',
    suggestedTags: ['event', 'invitation', 'networking']
  },
  gift: {
    name: 'Gift & Recognition',
    description: 'Send gifts and recognition items to alumni',
    icon: 'gift',
    suggestedTags: ['gift', 'recognition', 'appreciation']
  }
};

const CreateCampaignModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    targetCount: 0,
    completedCount: 0,
    status: 'planned',
    tags: [],
    campaignType: 'general',
    sendosoEnabled: false,
    sendosoGiftType: '',
    sendosoGiftBudget: 0
  });
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data directly
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Special handling for campaign type changes
    if (name === 'campaignType') {
      // Get suggested tags for the selected campaign type
      const campaignTypeInfo = CAMPAIGN_TYPES[value];
      if (campaignTypeInfo && campaignTypeInfo.suggestedTags) {
        // Add suggested tags if they don't already exist
        const existingTags = new Set(formData.tags);
        const newTags = campaignTypeInfo.suggestedTags.filter(tag => !existingTags.has(tag));
        
        if (newTags.length > 0) {
          setFormData(prev => ({
            ...prev,
            tags: [...prev.tags, ...newTags]
          }));
        }
      }
      
      // If it's a gift or anniversary campaign, enable Sendoso by default
      if (value === 'gift' || value === 'anniversary') {
        setFormData(prev => ({
          ...prev,
          sendosoEnabled: true,
          // Set default gift type for anniversary campaigns
          sendosoGiftType: value === 'anniversary' ? 'anniversary_plaque' : prev.sendosoGiftType,
          // Set default budget for anniversary campaigns
          sendosoGiftBudget: value === 'anniversary' ? 100 : prev.sendosoGiftBudget
        }));
      } else {
        // Disable Sendoso for other campaign types
        setFormData(prev => ({
          ...prev,
          sendosoEnabled: false,
          sendosoGiftType: '',
          sendosoGiftBudget: 0
        }));
      }
    }
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Handle tag input
  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  // Add a tag
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim().toLowerCase())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim().toLowerCase()]
      });
      setTagInput('');
    }
  };

  // Remove a tag
  const removeTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  // Handle tag input keydown (add tag on Enter)
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Campaign name is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    } else if (formData.startDate && new Date(formData.endDate) < new Date(formData.startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    
    if (formData.targetCount <= 0) {
      newErrors.targetCount = 'Target count must be greater than 0';
    }
    
    // Validate Sendoso fields if enabled
    if (formData.sendosoEnabled) {
      if (!formData.sendosoGiftType) {
        newErrors.sendosoGiftType = 'Please select a gift type';
      }
      
      if (formData.sendosoGiftBudget <= 0) {
        newErrors.sendosoGiftBudget = 'Gift budget must be greater than 0';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave({
        ...formData,
        targetCount: parseInt(formData.targetCount, 10)
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 overflow-y-auto max-h-[90vh]">
        <div className="bg-primary text-white px-6 py-4 flex justify-between items-center sticky top-0 z-10">
          <h3 className="text-xl font-semibold">Create New Campaign</h3>
          <button 
            onClick={onClose}
            className="text-white hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
              Campaign Name*
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Enter campaign name"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="campaignType">
              Campaign Type*
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {Object.entries(CAMPAIGN_TYPES).map(([type, details]) => (
                <div 
                  key={type}
                  onClick={() => handleChange({ target: { name: 'campaignType', value: type } })}
                  className={`cursor-pointer border rounded-lg p-4 transition-all h-full flex flex-col ${formData.campaignType === type ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}
                >
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-2">
                      {details.icon === 'mail' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                        </svg>
                      )}
                      {details.icon === 'cake' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z"></path>
                        </svg>
                      )}
                      {details.icon === 'newspaper' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"></path>
                        </svg>
                      )}
                      {details.icon === 'heart' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                        </svg>
                      )}
                      {details.icon === 'briefcase' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                        </svg>
                      )}
                      {details.icon === 'calendar' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                      )}
                      {details.icon === 'gift' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
                        </svg>
                      )}
                    </div>
                    <h3 className="font-medium text-gray-800">{details.name}</h3>
                  </div>
                  <p className="text-sm text-gray-500 ml-10 mt-1">{details.description}</p>
                </div>
              ))}
            </div>
            <input type="hidden" name="campaignType" value={formData.campaignType} />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
              Description*
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Enter campaign description"
              rows="4"
            ></textarea>
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="startDate">
                Start Date*
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${errors.startDate ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="yyyy-mm-dd"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
              </div>
              {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>}
            </div>
            
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="endDate">
                End Date*
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${errors.endDate ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="yyyy-mm-dd"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
              </div>
              {errors.endDate && <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>}
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="targetCount">
                Target Count*
              </label>
              <input
                type="number"
                id="targetCount"
                name="targetCount"
                value={formData.targetCount}
                onChange={handleChange}
                min="1"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${errors.targetCount ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Number of contacts"
              />
              {errors.targetCount && <p className="text-red-500 text-xs mt-1">{errors.targetCount}</p>}
            </div>
            
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="status">
                Status
              </label>
              <div className="relative">
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary appearance-none"
                >
                  <option value="planned">Planned</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="paused">Paused</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sendoso Integration for Gift Campaigns */}
          {formData.campaignType === 'gift' || formData.campaignType === 'anniversary' ? (
            <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
                  </svg>
                  <h3 className="font-medium text-blue-800">Physical Gift Integration</h3>
                </div>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input 
                    type="checkbox" 
                    name="sendosoEnabled" 
                    id="sendosoEnabled" 
                    checked={formData.sendosoEnabled}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        sendosoEnabled: e.target.checked
                      });
                    }}
                    className="checked:bg-blue-500 outline-none focus:outline-none right-4 checked:right-0 duration-200 ease-in absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label htmlFor="sendosoEnabled" className="block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                </div>
              </div>
              
              {formData.sendosoEnabled && (
                <div className="pl-7">
                  <p className="text-sm text-blue-700 mb-3">
                    Enable Sendoso integration to send physical gifts to alumni as part of this campaign.
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="sendosoGiftType">
                        Gift Type
                      </label>
                      <select
                        id="sendosoGiftType"
                        name="sendosoGiftType"
                        value={formData.sendosoGiftType}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">Select a gift type</option>
                        <option value="company_swag">Company Branded Swag</option>
                        <option value="gift_card">Gift Card</option>
                        <option value="anniversary_plaque">Anniversary Plaque</option>
                        <option value="custom_gift_box">Custom Gift Box</option>
                        <option value="wine_basket">Wine Basket</option>
                        <option value="tech_gadget">Tech Gadget</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="sendosoGiftBudget">
                        Gift Budget (per person)
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500">$</span>
                        </div>
                        <input
                          type="number"
                          id="sendosoGiftBudget"
                          name="sendosoGiftBudget"
                          value={formData.sendosoGiftBudget}
                          onChange={handleChange}
                          min="0"
                          step="5"
                          className="w-full pl-7 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Budget per recipient"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 p-3 rounded-md border border-yellow-100 text-sm text-yellow-700">
                    <p className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      Total estimated budget: ${formData.targetCount * formData.sendosoGiftBudget || 0}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Tags
            </label>
            <div className="flex">
              <input
                type="text"
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagKeyDown}
                className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Add tags (press Enter)"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-r-md hover:bg-gray-300"
              >
                Add
              </button>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.tags.map((tag, index) => (
                <div 
                  key={index} 
                  className="bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-sm flex items-center"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-1 text-blue-700 hover:text-blue-900"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700"
            >
              Create Campaign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCampaignModal;
